import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import { Box, Button, Chip, Link, Modal, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import Table from "components/Table";
import NewPortfolioModal from "components/Modals/NewPortfolio";
import MainLayout from "layouts/Main";
import { NORD_POOL_TABS } from "helpers/tabs";
import { selectPortfolios } from "store/selectors";
import { fetchPortfolios } from "store/appSlice";
import { ProtectedPaths } from "routes";

const PortfoliosTable = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  const dispatch = useAppDispatch();
  const portfolios = useAppSelector(selectPortfolios);

  useEffect(() => {
    dispatch(fetchPortfolios());
  }, []);

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: (row: any) => {
        return (
          <Link href={`/${ProtectedPaths.Portfolios}/${row.row.original.id}`}>
            {row.row.original.name}
          </Link>
        );
      },
    },
    {
      accessorKey: "clients",
      header: "Clients",
      cell: (row: any) => {
        const linkedClients = row.row.original.clients;

        return (
          <>
            {linkedClients.slice(0, 2).map((value: any) => (
              <Link
                href={`/${ProtectedPaths.Clients}/${value.id}`}
                key={`ClientChip-${value.id}`}
              >
                <Chip
                  label={value.name}
                  size="small"
                  sx={{ mr: 1 }}
                  clickable
                />
              </Link>
            ))}
            {linkedClients.length > 2 && (
              <Chip
                label={`+ ${linkedClients.length - 2}`}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "nordpoolNames",
      header: "Nord Pool portfolios",
      cell: (row: any) => {
        const nordpoolNames = row.row.original.nordpoolNames;

        return (
          <>
            {nordpoolNames.slice(0, 2).map((value: any) => (
              <Chip label={value.name} size="small" sx={{ mr: 1 }} />
            ))}
            {nordpoolNames.length > 2 && (
              <Chip
                label={`+ ${nordpoolNames.length - 2}`}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "prognoseTemplates",
      header: "Consumption forecast samples",
      cell: (row: any) => {
        const savedSamples = row.row.original.prognoseTemplates;

        return (
          <>
            {savedSamples.slice(0, 2).map((value: any) => (
              <Chip
                label={value.name}
                key={`ClientChip-${value.id}`}
                size="small"
                sx={{ mr: 1 }}
              />
            ))}
            {savedSamples.length > 2 && (
              <Chip
                label={`+ ${savedSamples.length - 2}`}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "visibleInExternalApis",
      header: "Visible in external APIs",
      cell: (row: any) => {
        const visible = row.row.original.visibleInExternalApis;
        return visible ? (
          <CheckCircleIcon color="success" fontSize="small" />
        ) : (
          <CancelIcon color="disabled" fontSize="small" />
        );
      },
    },
  ];

  return (
    <MainLayout tabs={NORD_POOL_TABS}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Portfolios</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          Add portfolio
        </Button>
      </Box>
      <Table
        data={portfolios}
        columns={columns}
        isFetching={!portfolios}
        pageCount={1}
        page={setCurrentPage}
        searchLabel="Search portfolios"
      />
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <NewPortfolioModal
          setOpenModal={setOpenModal}
          dispatch={() => dispatch(fetchPortfolios())}
        />
      </Modal>
    </MainLayout>
  );
};

export default PortfoliosTable;
